'use client';

import React, { useState } from 'react';
import { v1, v3, v4, v5, NIL as NIL_UUID } from 'uuid';
import AutoGrowingTextarea from '@/components/ui/AutoGrowingTextarea';
import GenerateButton from '@/components/ui/GenerateButton';

interface UUIDOptions {
    version: 'v1' | 'v3' | 'v4' | 'v5' | 'nil';
    count: number;
    uppercase: boolean;
    namespace?: string;
    name?: string;
}

function GenerateUUIDPage() {
    const [options, setOptions] = useState<UUIDOptions>({
        version: 'v4',
        count: 1,
        uppercase: false,
        namespace: '6ba7b810-9dad-11d1-80b4-00c04fd430c8', // DNS namespace
        name: 'example.com',
    });

    const [generatedUUIDs, setGeneratedUUIDs] = useState<string>('');

    const handleOptionChange = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value, type } = event.target;
        setOptions(prevOptions => ({
            ...prevOptions,
            [name]: type === 'checkbox' ? (event.target as HTMLInputElement).checked 
                  : type === 'number' ? parseInt(value) 
                  : value,
        }));
    };

    const generateUUID = (version: UUIDOptions['version']): string => {
        switch (version) {
            case 'v1':
                return v1();
            case 'v3':
                return v3(options.name || '', options.namespace || '');
            case 'v4':
                return v4();
            case 'v5':
                return v5(options.name || '', options.namespace || '');
            case 'nil':
                return NIL_UUID;
            default:
                return v4();
        }
    };

    const generateUUIDs = () => {
        if (options.count < 1 || options.count > 100) {
            return;
        }
        let uuids: string[];
        if (options.version === 'v3' || options.version === 'v5' || options.version === 'nil') {
            uuids = [generateUUID(options.version)];
        } else {
            uuids = Array.from({ length: options.count }, () => generateUUID(options.version));
        }
        const formattedUUIDs = uuids.map(uuid => options.uppercase ? uuid.toUpperCase() : uuid).join('\n');
        setGeneratedUUIDs(formattedUUIDs);
    };

    const isNameBased = options.version === 'v3' || options.version === 'v5';
    const isNil = options.version === 'nil';

    return (
        <div className="w-full p-4">
            <div className='flex-grow flex justify-center mb-6'>
                <h1 className="text-2xl font-bold">Generate UUIDs</h1>
            </div>
            <div className="flex flex-col md:flex-row gap-6">
                {/* Left Column - Configuration */}
                <div className="w-full md:w-1/2">
                    <div className="form-control mb-4">
                        <h2 className="text-xl font-semibold mb-2">Options</h2>
                        <label className="label">
                            <span className="label-text">UUID Version</span>
                        </label>
                        <select 
                            className="select select-bordered w-full" 
                            name="version" 
                            value={options.version} 
                            onChange={handleOptionChange}
                        >
                            <option value="v1">Version 1 (time-based)</option>
                            <option value="v3">Version 3 (name-based, MD5)</option>
                            <option value="v4">Version 4 (random)</option>
                            <option value="v5">Version 5 (name-based, SHA-1)</option>
                            <option value="nil">Nil UUID</option>
                        </select>
                    </div>
                    {!isNameBased && !isNil && (
                        <div className="form-control mb-4">
                            <label className="label">
                                <span className="label-text">Number of UUIDs (1-100)</span>
                            </label>
                            <input 
                                type="number" 
                                className={`input input-bordered w-full ${options.count < 1 || options.count > 100 ? 'input-error' : ''}`}
                                name="count" 
                                min="1" 
                                max="100"
                                value={options.count} 
                                onChange={handleOptionChange} 
                            />
                        </div>
                    )}
                    <div className="form-control mb-4">
                        <label className="label cursor-pointer justify-start">
                            <input 
                                type="checkbox" 
                                className="checkbox checkbox-sm mr-2" 
                                name="uppercase" 
                                checked={options.uppercase} 
                                onChange={handleOptionChange} 
                            />
                            <span className="label-text">Uppercase</span>
                        </label>
                    </div>
                    {isNameBased && (
                        <>
                            <div className="form-control mb-4">
                                <label className="label">
                                    <span className="label-text">Namespace UUID</span>
                                </label>
                                <input 
                                    type="text" 
                                    className="input input-bordered w-full" 
                                    name="namespace" 
                                    value={options.namespace} 
                                    onChange={handleOptionChange} 
                                />
                            </div>
                            <div className="form-control mb-4">
                                <label className="label">
                                    <span className="label-text">Name</span>
                                </label>
                                <input 
                                    type="text" 
                                    className="input input-bordered w-full" 
                                    name="name" 
                                    value={options.name} 
                                    onChange={handleOptionChange} 
                                />
                            </div>
                        </>
                    )}
                    <GenerateButton generatePasswords={generateUUIDs} />
                </div>

                {/* Right Column - Generated UUIDs */}
                <div className="w-full md:w-1/2">
                    <h2 className="text-xl font-semibold mb-2">Generated UUIDs</h2>
                    <AutoGrowingTextarea
                        value={generatedUUIDs}
                        minHeight={128}
                        maxHeight={512}
                    />
                </div>
            </div>
        </div>
    );
}

export default GenerateUUIDPage;